import { defineComponent as _defineComponent } from 'vue'
import { translateLang } from '@/data/i18n/translateLang'
import { PartType } from '@/partTypes'
import store from '@/store'
import { getSawKnife } from '@/util/LayoutFuncs'
import { buryPointApi, toDecimal, verifyInputNumber } from '@/util/commonFuncs'
import {
  dealBigPlank,
  dealPlankPoly,
  getPlateKnifeDiameter,
} from '@/util/dealPaibanData'
import { calcBigpartArea } from '@/util/drawPlankFuncs'
import { message } from 'ant-design-vue'
import { computed, reactive, ref } from 'vue'

import { isBrandBigPlank } from '../util/layoutDataDeal'
import { getPlankSizeOpration } from '../util/plankSizeOpration'


export default /*#__PURE__*/_defineComponent({
  __name: 'm-batch-edit-size',
  props: {
    isShow: { type: Boolean, default: false }
  },
  emits: ["update:isShow", "onPaibanDataUpdate"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'update:isShow', val: boolean): void
  (e: 'onPaibanDataUpdate', val: any[], editPlankList: any[]): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isShow: boolean }





const plankTypeList = computed(() => {
  const stockKeyList = store.state.paibanData
    .filter(
      (item) =>
        (!item.surplusInfo || !item.surplusInfo.is_plus) &&
        !isBrandBigPlank(item)
    )
    .map((item) => {
      const newArr = item.stockKey.split(':').slice(0, 3)
      newArr.push(`(${item.plankWidth}*${item.plankHeight})`)
      return newArr.join(':')
    })
  const result = Array.from(new Set(stockKeyList)) as string[]

  return result.map((str) => {
    return {
      label: str,
      value: str.split(':').slice(0, -1).join(':'),
      key: str,
    }
  })
})

const isVisible = computed({
  get() {
    return props.isShow
  },
  set(val) {
    emits('update:isShow', val)
  },
})

/** 大板类型 */
const plankType = ref<string | undefined>(undefined)

/** 表单 */
const form = reactive({
  modifyType: '1',
  startIndex: '',
  endIndex: '',
  width: 0,
  height: 0,
  plankType: '',
})

const initForm = () => {
  form.width = 0
  form.height = 0
  form.startIndex = ''
  form.endIndex = ''
  form.plankType = ''
  plankType.value = undefined
}

/** 需要修改的大板列表 */
const editPlankList = computed<any[]>(() => {
  const paibanData = store.state.paibanData.filter(
    (item) =>
      !(item.surplusInfo && item.surplusInfo.is_plus) && !isBrandBigPlank(item)
  )

  const { modifyType, plankType, startIndex, endIndex } = form
  let allPlanks = []
  // 如果是按照类型修改

  if (modifyType === '1') {
    if (plankType === 'all') {
      // 所有大板
      allPlanks = paibanData
    } else if (plankType) {
      allPlanks = paibanData.filter((plank) => {
        const stockKey = plankType.split(':').slice(0, 3).join(':')
        const sizeStr = plankType.split(':').slice(-1)[0]
        const plankSize = sizeStr.replace(/[()]/g, '')
        const plankW = plankSize.split('*')[0]
        const plankH = plankSize.split('*')[1]
        return (
          plank.stockKey.includes(stockKey) &&
          plank.plankWidth == plankW &&
          plank.plankHeight == plankH
        )
      })
    }
  } else {
    if (Number(startIndex) && Number(endIndex)) {
      allPlanks = paibanData.filter(
        (plank) =>
          plank.canvasIndex >= startIndex && plank.canvasIndex <= endIndex
      )
    } else {
      if (startIndex) {
        allPlanks = paibanData.filter(
          (plank) => plank.canvasIndex == startIndex
        )
      }
      if (endIndex) {
        allPlanks = paibanData.filter((plank) => plank.canvasIndex == endIndex)
      }
    }
  }

  return allPlanks
})

/** 按大板号修改时，选中范围内被跳过的品牌大板数量（品牌大板不支持修改尺寸） */
const skippedBrandPlankCount = computed<number>(() => {
  const { modifyType, startIndex, endIndex } = form
  if (modifyType === '1') return 0
  const paibanData = store.state.paibanData.filter(
    (item) => !(item.surplusInfo && item.surplusInfo.is_plus)
  )
  let rangePlanks: any[] = []
  if (Number(startIndex) && Number(endIndex)) {
    rangePlanks = paibanData.filter(
      (plank) =>
        plank.canvasIndex >= startIndex && plank.canvasIndex <= endIndex
    )
  } else if (startIndex) {
    rangePlanks = paibanData.filter((plank) => plank.canvasIndex == startIndex)
  } else if (endIndex) {
    rangePlanks = paibanData.filter((plank) => plank.canvasIndex == endIndex)
  }
  return rangePlanks.filter((plank) => isBrandBigPlank(plank)).length
})

/** 最小宽度限制 */
const minWidth = computed(() => {
  const ncSetting = store.state.ncSetting
  /** 余料摆放位置 */
  const surplusPosition = String(ncSetting.surplusPosition)
  /** 修改的大板 */
  const allPlankList = editPlankList.value

  /** 是否只有一种下料刀 */
  const isOnlyOneKnife =
    allPlankList.length && checkIsOnlyOneKnife(allPlankList)

  if (!isOnlyOneKnife) return 600

  /** 下料刀直径 */
  const diameter = allPlankList[0].isUseSaw
    ? getSawKnife(ncSetting)
    : getPlateKnifeDiameter(allPlankList[0].stockKey, store.state.ncSetting)
  const minWidthList = allPlankList.map((plank) => {
    /** 修边数组（可能是四周修边的大板） */
    const marginArr = [] as string[]
    if (String(plank.margin).includes(',')) {
      marginArr.push(...String(plank.margin).split(','))
    } else {
      marginArr.push(String(plank.margin))
    }
    if (surplusPosition.includes('Left')) {
      const minX = Math.min(
        ...plank.parts
          .filter(
            (p: PartType & { blueAreaSurplus: boolean }) => !p.blueAreaSurplus
          )
          .map((p: PartType) => p.startX)
      )
      /** 大板修边 */
      const margin =
        marginArr.length > 1 ? Number(marginArr[2]) : Number(marginArr[0])
      return toDecimal(
        plank.parts.length
          ? plank.plankWidth - minX - (diameter / 2 - margin)
          : 1,
        2
      )
    } else {
      const maxX = Math.max(
        ...plank.parts
          .filter(
            (p: PartType & { blueAreaSurplus: boolean }) => !p.blueAreaSurplus
          )
          .map((p: PartType) => p.startX + p.rect.width)
      )
      /** 大板修边 */
      const margin =
        marginArr.length > 1 ? Number(marginArr[3]) : Number(marginArr[0])
      return toDecimal(
        plank.parts.length ? maxX - (diameter / 2 - margin) : 1,
        2
      )
    }
  })

  // setMinWidth(Math.max(...minWidthList))
  return Math.max(...minWidthList)
})

/** 最小高度限制 */
const minHeight = computed(() => {
  const ncSetting = store.state.ncSetting
  /** 余料摆放位置 */
  const surplusPosition = String(ncSetting.surplusPosition)
  const allPlankList = editPlankList.value

  /** 是否只有一种下料刀 */
  const isOnlyOneKnife =
    allPlankList.length && checkIsOnlyOneKnife(allPlankList)
  /** 如果大板类型不相同，则返回4000 */
  if (!isOnlyOneKnife) return 4000

  const diameter = allPlankList[0].isUseSaw
    ? getSawKnife(ncSetting)
    : getPlateKnifeDiameter(allPlankList[0].stockKey, store.state.ncSetting)

  const minHeightList = allPlankList.map((plank) => {
    /** 修边数组（可能是四周修边的大板） */
    const marginArr = [] as string[]
    if (String(plank.margin).includes(',')) {
      marginArr.push(...String(plank.margin).split(','))
    } else {
      marginArr.push(String(plank.margin))
    }

    if (surplusPosition.includes('top')) {
      const minY = Math.min(
        ...plank.parts
          .filter(
            (p: PartType & { blueAreaSurplus: boolean }) => !p.blueAreaSurplus
          )
          .map((p: PartType) => p.startY)
      )

      const margin = Number(marginArr[0])

      return toDecimal(
        plank.parts.length
          ? plank.plankHeight - minY - (diameter / 2 - margin)
          : 1,
        2
      )
    } else {
      const maxY = Math.max(
        ...plank.parts
          .filter(
            (p: PartType & { blueAreaSurplus: boolean }) => !p.blueAreaSurplus
          )
          .map((p: PartType) => p.startY + p.rect.height)
      )

      const margin =
        marginArr.length > 1 ? Number(marginArr[1]) : Number(marginArr[0])
      return toDecimal(
        plank.parts.length ? maxY - (diameter / 2 - margin) : 1,
        2
      )
    }
  })

  // setMinHeight(Math.max(...minHeightList))
  return Math.max(...minHeightList)
})

// const setMinWidth = (val: number) => {
//   form.width = val
// }

// const setMinHeight = (val: number) => {
//   form.height = val
// }

/** 修改大板数量是否为空 */
const isPlankListEmpty = computed(() => {
  return editPlankList.value.length === 0
})

/** 是否只有一种材质颜色厚度的大板 */
const isOnlyOneKnife = computed(() => {
  const plankList = editPlankList.value
  return checkIsOnlyOneKnife(plankList)
})

/**
 * 检查大板列表是否只有一种下料刀
 * @param plankList 大板列表
 */
const checkIsOnlyOneKnife = (plankList: any[]) => {
  if (plankList.length) {
    const diameterList = plankList
      .map((plank) => {
        const ncSetting = store.state.ncSetting
        if (plank.isUseSaw) {
          return Number(getSawKnife(ncSetting))
        } else {
          return Number(getPlateKnifeDiameter(plank.stockKey, ncSetting))
        }
      })
      .filter((item) => item)
    return Array.from(new Set(diameterList)).length === 1
  } else {
    return true
  }
}

/** 确认修改 */
const handleOk = () => {
  const isValidate = checkFormisValid()
  if (!isValidate) return

  if (editPlankList.value.length === 0) {
    message.warning(translateLang('没有符合条件的大板可修改'))
    return
  }

  if (skippedBrandPlankCount.value) {
    message.warning(
      translateLang('选中范围内有{count}张品牌大板不支持修改尺寸，已跳过', {
        count: skippedBrandPlankCount.value,
      })
    )
  }

  const recordDataArr: any[] = []
  editPlankList.value.forEach((plank) => {
    const changedWidth = form.width - plank.plankWidth
    const changedHeight = form.height - plank.plankHeight
    const recordData = getPlankSizeOpration(plank, form.width, form.height)
    recordDataArr.push(recordData)
    plank.parts = plank.parts.filter(
      (p: PartType & { blueAreaSurplus: boolean }) => !p.blueAreaSurplus
    )
    const surplusPosition = String(store.state.ncSetting.surplusPosition)
    if (surplusPosition.includes('Left')) {
      plank.parts.forEach((part: PartType) => {
        part.startX = part.startX + changedWidth
        dealPlankPoly(part)
      })
    }
    if (surplusPosition.includes('top')) {
      plank.parts.forEach((part: PartType) => {
        part.startY = part.startY + changedHeight
        dealPlankPoly(part)
      })
    }

    plank.plankWidth = Number(form.width)
    plank.plankHeight = Number(form.height)
    plank.path = [
      {
        x: 0,
        y: 0,
      },
      { x: plank.plankWidth, y: 0 },
      { x: plank.plankWidth, y: plank.plankHeight },
      { x: 0, y: plank.plankHeight },
      { x: 0, y: 0 },
    ]
    plank.big_plank_size = {
      width: form.width,
      height: form.height,
      otherPlate: true,
    }
    plank.hasEdited = true
    plank.bigPlank = [window.$copy(plank.path)]
    const { usedRate, usedArea } = calcBigpartArea(plank, store.state.ncSetting)
    plank.usedRate = usedRate
    plank.usedArea = usedArea
    dealBigPlank(plank)
    dealPlankPoly(plank)
  })
  buryPointApi('layout', 'batch_edit_plank_size')
  emits('onPaibanDataUpdate', recordDataArr, editPlankList.value)
  initForm()
}

const handleVerifyInput = (key: 'width' | 'height') => {
  verifyInputNumber(String(form[key]), form, [key], '4-2')
}

/** 改变大板类型（材质颜色厚度） */
const handleModifyTypeChange = () => {
  if (plankType.value) {
    form.plankType = plankType.value
  }
}

const checkFormisValid = () => {
  const isValidate =
    form.width >= minWidth.value && form.height >= minHeight.value
  if (!isValidate) {
    message.error('修改尺寸不能小于最小值')
  }
  return isValidate
}

return { __sfc: true,props, emits, plankTypeList, isVisible, plankType, form, initForm, editPlankList, skippedBrandPlankCount, minWidth, minHeight, isPlankListEmpty, isOnlyOneKnife, checkIsOnlyOneKnife, handleOk, handleVerifyInput, handleModifyTypeChange, checkFormisValid }
}

})